import React, { useState, useEffect } from 'react';
import { X, Loader2, AlertCircle, Download, ExternalLink } from 'lucide-react';
import { fetchMvrPdfBlobFromLog } from '../services/searchHistoryService';
import { getErrorMessage } from '../services/apiService';

interface MvrPdfViewerModalProps {
  isOpen: boolean;
  onClose: () => void;
  logId: string;
  vin: string;
  stateCode?: string | null;
}

const MvrPdfViewerModal: React.FC<MvrPdfViewerModalProps> = ({
  isOpen,
  onClose,
  logId,
  vin,
  stateCode,
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
  const [resolvedState, setResolvedState] = useState<string | null>(stateCode ?? null);

  useEffect(() => {
    if (!isOpen || !logId) return;

    let currentPdfUrl: string | null = null;

    const fetchPdf = async () => {
      setLoading(true);
      setError(null);
      setPdfUrl(null);
      setPdfBlob(null);

      try {
        const { blob, stateCode: stateCodeFromLog } = await fetchMvrPdfBlobFromLog(logId);
        const url = window.URL.createObjectURL(blob);
        currentPdfUrl = url;
        setPdfUrl(url);
        setPdfBlob(blob);
        setResolvedState(stateCodeFromLog);
      } catch (err: any) {
        setError(getErrorMessage(err));
      } finally {
        setLoading(false);
      }
    };

    fetchPdf();

    return () => {
      if (currentPdfUrl) {
        window.URL.revokeObjectURL(currentPdfUrl);
      }
    };
  }, [isOpen, logId]);

  const handleDownload = () => {
    if (!pdfBlob) return;
    const downloadUrl = window.URL.createObjectURL(pdfBlob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    const datePart = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    const stateForName = (resolvedState || stateCode || 'XX').toUpperCase();
    link.download = `MVR_${stateForName}_${vin}_${datePart}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  };

  const handleOpenInNewTab = () => {
    if (pdfUrl) {
      window.open(pdfUrl, '_blank');
    }
  };

  if (!isOpen) return null;

  const stateLabel = (resolvedState || stateCode || '').toUpperCase();

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-slate-900 rounded-xl shadow-2xl w-full max-w-6xl h-[90vh] flex flex-col border border-white/10">
        <div className="flex items-center justify-between p-4 border-b border-white/10 shrink-0">
          <div>
            <h2 className="text-xl font-bold text-white">
              Motor Vehicle Record{stateLabel ? ` · ${stateLabel}` : ''}
            </h2>
            <p className="text-sm text-slate-400 font-mono">{vin}</p>
          </div>
          <div className="flex items-center gap-2">
            {pdfUrl && (
              <>
                <button
                  onClick={handleOpenInNewTab}
                  className="flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white text-sm font-medium rounded-lg transition-colors border border-white/10"
                >
                  <ExternalLink className="w-4 h-4" />
                  Open in New Tab
                </button>
                <button
                  onClick={handleDownload}
                  className="flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white text-sm font-medium rounded-lg transition-colors border border-white/10"
                >
                  <Download className="w-4 h-4" />
                  Download
                </button>
              </>
            )}
            <button
              onClick={onClose}
              className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <Loader2 className="w-10 h-10 text-[#AD0000] animate-spin mx-auto mb-4" />
                <p className="text-slate-400">Generating MVR PDF...</p>
              </div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
                <p className="text-red-400 font-medium mb-2">Failed to generate report</p>
                <p className="text-slate-500 text-sm">{error}</p>
              </div>
            </div>
          ) : pdfUrl ? (
            <iframe
              src={`${pdfUrl}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
              className="w-full h-full border-0"
              title={`Motor Vehicle Record - ${vin}`}
            />
          ) : (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <AlertCircle className="w-12 h-12 text-yellow-400 mx-auto mb-4" />
                <p className="text-yellow-400 font-medium">No PDF available</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MvrPdfViewerModal;
